export abstract class DateFormatter {
    /**
     * Parses a date and time and returns the date as a string in ISO format with no change to the timezone
     * @param input The date to parse and format
     * @returns A string in the format "yyyy-MM-dd"
     */
    static isoDateLiteral(input: string): string {
        return new Date(this.ensureUtc(this.ensureFullDateTimeString(input))).toISOString().split("T")[0];
    }

    /**
     * Parses a date and time and returns the time as a string in ISO format with no change to the timezone
     * @param input The date for parse and format
     * @returns A string in the format "hh:mm:ss"
     */
    static isoTimeLiteral(input: string): string {
        return new Date(this.ensureUtc(this.ensureFullDateTimeString(input))).toISOString().split("T")[1].slice(0, 8);
    }

    /**
     * Parses a date and time and returns the date and time as a string in ISO format with no change to the timezone
     * @param input The date for parse and format
     * @returns A string in the format "hh:mm:ss"
     */
    static isoDateTimeLiteral(input: string): string {
        return new Date(this.ensureUtc(this.ensureFullDateTimeString(input))).toISOString();
    }

    /**
     * Parses a date and time and returns the date as a string in ISO format in the local timezone
     * @param input The date to parse and format
     * @returns A string adjusted to the current timezone in the format "yyyy-MM-dd"
     */
    static isoDateLocal(input: string | number | Date): string {
        return new Date(input).toLocaleDateString("sv-SE");
    }

    /**
     * Parses a date and time and returns the time as a string in ISO format in the local timezone
     * @param input The date to parse and format
     * @returns A string adjusted to the current timezone in the format "hh:mm:ss"
     */
    static isoTimeLocal(input: string | number | Date): string {
        return new Date(input).toLocaleTimeString("sv-SE");
    }

    /**
     * Parses a date and returns an ISO format DateTime in UTC
     * @param date The date to parse and format
     */
    static isoDateTimeUtc(date: string): string;
    /**
     * Parses a date and time and returns an ISO format DateTime in UTC
     * @param date The date to parse and format
     * @param time The time to parse and format
     */
    static isoDateTimeUtc(date: string, time: string): string;
    static isoDateTimeUtc(date: string, time?: string): string {
        let input = time ? this.isoDateLiteral(date) : this.isoDateTimeLiteral(date);
        if (time) {
            input += `T${time}`;
        }

        return new Date(this.ensureFullDateTimeString(input)).toISOString();
    }

    /**
     * Parses a date and time and returns the date as a string in the locale format and local timezone
     * @param input The date to parse and format
     * @returns A date string adjusted to the current timezone in the locale format
     */
    static localeDateLocal(input: string | number | Date): string {
        return new Date(input).toLocaleDateString();
    }

    /**
     * Parses a date and time and returns the date as a Month Year string in the locale format and local timezone
     * @param input The date to parse and format
     * @returns A Month/Year date string. e.g. July 2025
     */
    static localeDateMonthYearLocal(input: string | number | Date): string {
        return new Date(input).toLocaleDateString(undefined, { year: "numeric", month: "long" });
    }

    /**
     * Parses a date and time and returns the date as a Month Year string in the locale format and local timezone
     * @param input The date to parse and format
     * @returns A Month/Year date string. e.g. Wed 20
     */
    static localeDateDayDateLocal(input: string | number | Date): string {
        return new Date(input).toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
    }

    /**
     * Parses a date and time and returns the time as a string in the locale format and local timezone
     * @param input The date and time to parse and format
     * @returns A time string adjusted to the current timezone in the locale format (hh:mm)
     */
    static localeTimeLocal(input: string | number | Date): string {
        return new Date(input).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    }

    /**
     * Parses a date and time and returns the date as a string in the locale format with no change to the timezone
     * @param input The date to parse and format
     * @returns A date string in the locale format with no change to the timezone
     */
    static localeDateLiteral(input: string): string {
        return new Date(this.ensureLocal(this.ensureFullDateTimeString(input))).toLocaleDateString();
    }

    /**
     * Parses a date and time and returns the time as a string in the locale format with no change to the timezone
     * @param input The date and time to parse and format
     * @returns A time string in the locale format with no change to the timezone
     */
    static localeTimeLiteral(input: string): string {
        return new Date(this.ensureLocal(this.ensureFullDateTimeString(input))).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    }

    /** Parses a date and then returns a natural string (e.g. tomorrow at 10pm) */
    static naturalDateAndTime(input: string | number | Date, {
        phrasesMap = {
            tomorrow: "tomorrow",
            at: "at",
        },
    }): string {
        const targetDate = new Date(input);
        const target = new Date(targetDate);
        const ref = new Date();

        target.setHours(0, 0, 0, 0);
        ref.setHours(0, 0, 0, 0);

        const dayDiff = Math.floor((target.getTime() - ref.getTime()) / (1000 * 60 * 60 * 24));

        const hasMinutes = targetDate.getMinutes() > 0;

        const time = new Intl.DateTimeFormat(undefined, {
            hour: "numeric",
            minute: hasMinutes ? "numeric" : undefined,
            hour12: true
        }).format(targetDate);

        const weekday = new Intl.DateTimeFormat(undefined, {
            weekday: "long",
        }).format(targetDate);

        const fullDate = new Intl.DateTimeFormat(undefined, {
            weekday: "long",
            day: "numeric",
            month: "long",
        }).format(targetDate);

        if (dayDiff === 0) return time;
        if (dayDiff === 1) return `${phrasesMap.tomorrow} ${phrasesMap.at} ${time}`;
        if (dayDiff >= 2 && dayDiff <= 6) return `${weekday} ${phrasesMap.at} ${time}`;

        return `${fullDate} ${phrasesMap.at} ${time}`;
    }

    /** Parses a date and then returns a natural time string (e.g. 10 pm or 9:23 am) */
    static naturalTime(input: string | number | Date): string {
        const targetDate = new Date(input);
        const hasMinutes = targetDate.getMinutes() > 0;

        return new Intl.DateTimeFormat(undefined, {
            hour: "numeric",
            minute: hasMinutes ? "numeric" : undefined,
            hour12: true
        }).format(targetDate);
    }

    // This allows us to pass yyyy-mm-dd (e.g. from an <input type="date" />) and have it work in Safari (which doesn't always support just the date)
    private static ensureFullDateTimeString(input: string): string {
        // Date only (YYYY-MM-DD) → add midnight
        if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
            return `${input}T00:00:00`;
        }

        // Otherwise, leave it as it is
        return input;
    }

    private static ensureUtc(input: string): string {
        return input.endsWith("Z") ? input : `${input}Z`;
    }

    private static ensureLocal(input: string): string {
        return !input.endsWith("Z") ? input : input.slice(0, -1);
    }
}